<script setup lang="ts">
import type { HouiQuery, PlanListQuery } from "~/types";
import { priceRange } from "~/constants/plan";
import useLetterAvg from "~/composables/useLetterAvg";

const attrs = await usePlanAttr();
const route = useRoute();

const letterAvg = await useLetterAvg();

let cachedComputedQuery: PlanListQuery;

const selectedQuery = computed(() => {
  const off = route.query.off ? parseInt(route.query.off.toString()) : 0;
  const range = route.query.range ? parseInt(route.query.range.toString()) : 0;
  const tsubo = route.query.tsubo ? parseInt(route.query.tsubo.toString()) : 0;
  const ktype = route.query.ktype ? parseInt(route.query.ktype.toString()) : 0;
  const kuruma = route.query.kuruma
    ? parseInt(route.query.kuruma.toString())
    : 0;
  const houi = route.query.houi
    ? (route.query.houi.toString() as HouiQuery)
    : "";
  const attr = route.query.attr ? route.query.attr.toString() : "";
  const order = route.query.order ? route.query.order.toString() : "";
  const asc = route.query.asc ? parseInt(route.query.asc.toString()) : 0;

  if (
    cachedComputedQuery &&
    cachedComputedQuery.off === off &&
    cachedComputedQuery.range === range &&
    cachedComputedQuery.tsubo === tsubo &&
    cachedComputedQuery.ktype === ktype &&
    cachedComputedQuery.kuruma === kuruma &&
    cachedComputedQuery.houi === houi &&
    cachedComputedQuery.attr === attr &&
    cachedComputedQuery.order === order &&
    cachedComputedQuery.asc === asc
  ) {
    return cachedComputedQuery;
  }

  return (cachedComputedQuery = {
    range,
    tsubo,
    ktype,
    kuruma,
    houi,
    attr,
    off,
    order,
    asc,
  });
});

const { data: plans, pending } = await useFetch("/api/plan/list", {
  query: selectedQuery,
  watch: [selectedQuery],
});

// パンくず
const breadcrumbs = computed(() => {
  const range = priceRange.find((e) => e.id == selectedQuery.value.range);
  const list = [
    { name: "TOP", path: "/" },
    { name: "プラン", path: "/plan" },
  ];

  if (range?.id) {
    list.push({
      name: range?.id ? range.name : "すべての価格帯",
      path: `/plan${range?.id ? `?range=${range?.id}` : ""}`,
    });
  }
  return list;
});

useBreadcrumbs(breadcrumbs.value);

// rangeが変わったらパンくずを書き換える
watch(
  () => selectedQuery.value.range,
  () => {
    useBreadcrombsForce(breadcrumbs.value);
  },
);

// meta関連

// タイトルを作成
const titleText = computed(() => {
  let title = "";
  title += plans.value?.query?.range?.name ?? "";
  title += plans.value?.query?.tsubo?.name ?? "";
  title += plans.value?.query.kuruma?.name
    ? `車${plans.value.query.kuruma.name}`
    : "";
  title += plans.value?.query.houi ? `${plans.value.query.houi}向き` : "";

  plans.value?.query.attr.forEach((item) => {
    const attr = attrs.value.find((attr) => attr.attr_num == item);
    if (!attr) return;
    if (title) {
      title += "の";
    }

    if (attr?.category === 1) {
      title += `${attr.cate_name}外構`;
    } else if (attr?.category === 2) {
      title += `${attr.cate_name}デザイン`;
    } else if (attr?.category === 3) {
      title += `${attr.cate_name}リフォーム`;
    } else if (attr?.category === 6) {
      title += `${attr.cate_name}工事`;
    }
  });

  return `${title ? `${title}の` : "外構・リフォームの"}プラン事例を紹介`;
});

const activeRangeGrade = computed(() => {
  return priceRange.find((e) => e.id == selectedQuery.value.range);
});

const description = computed(() => {
  if (activeRangeGrade.value) {
    // 共通部分を生成
    const head = `
    【公式】
    ${activeRangeGrade.value.from ? activeRangeGrade.value.from + "万～" : ""}
    ${activeRangeGrade.value.to ? activeRangeGrade.value.to + "万以下" : ""}
    の外構工事プラン一覧の${selectedQuery.value.off + 1}ページ目です。`;

    const end = `ファミリー庭園は多様なプランや見積りを無料でご提案。クチコミ平均★${letterAvg.value.avg.enq_1}、工事品質には自信があります！`;

    return head + activeRangeGrade.value.description + end;
  }

  return `${titleText.value}です。${
    selectedQuery.value.off ? `${selectedQuery.value.off + 1}ページ目` : ""
  }`;
});

useHead({
  // タイトルは動的に変更
  title: computed(() => titleText.value),
  meta: [
    {
      name: "description",
      content: description,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: `https://www.famitei.co.jp/plan?range=${selectedQuery.value.range}&tsubo=${selectedQuery.value.tsubo}&kuruma=${selectedQuery.value.kuruma}&houi=${selectedQuery.value.houi}&attr=${selectedQuery.value.attr}&off=${selectedQuery.value.off}&order=${selectedQuery.value.order}&asc=${selectedQuery.value.asc}`,
    },
  ],
});

useScrollTop(true);
</script>

<template>
  <div class="wrapper" v-if="plans">
    <TextPageTitle class="hidden pc:block">プラン一覧</TextPageTitle>
    <!-- 価格帯コメント -->
    <PlanRangeComment
      class="hidden pc:block mb-8"
      :range="activeRangeGrade"
      :query="selectedQuery"
    />

    <div class="flex flex-col pc:flex-row">
      <ButtonPlanFilter />
      <!-- PCサイドメニュー -->
      <PlanMenuSide
        :query="selectedQuery"
        class="hidden pc:block pc:w-1/4 px-4"
      />

      <div class="w-full min-h-screen pc:w-3/4 pc:px-4 relative">
        <!-- SPメニュー -->
        <div class="pc:hidden mb-8">
          <p class="font-bold">価格帯から選ぶ</p>
          <PlanMenuPriceList class="mb-4" />

          <!-- 価格帯コメント -->
          <PlanRangeComment
            class="mb-8"
            :range="activeRangeGrade"
            :query="selectedQuery"
            :accordion="true"
          />

          <PlanMenuFilterOpen class="mb-4" />
          <p class="font-bold">並び替え</p>
          <PlanSelectSort />
        </div>

        <div class="flex justify-between items-end px-2 y-2 mb-2">
          <div class="whitespace-nowrap overflow-hidden">
            <!-- 現在のフィルタラベル -->
            <PlanLabel :query="plans.query" class="" />

            <!-- 件数 -->
            <p class="text-sm">
              <span class="text-2xl bold">{{ plans.count }}</span>
              件
            </p>
          </div>
          <PaginationCompact
            :params="{
              count: plans.count,
              current: selectedQuery.off,
              max: plans.max,
              perPage: 20,
            }"
          />
        </div>

        <ListResponsiveWrapper v-if="!pending" :tablet="2" :pc="2">
          <PlanDetailLink
            v-for="(plan, index) in plans.list"
            :key="plan.plan_cd"
            :plan="plan"
            :index="index"
          />
        </ListResponsiveWrapper>
        <div
          v-if="!pending && !plans.list.length"
          class="w-full text-center py-32"
        >
          <p class="mb-4">この条件に一致する項目は見つかりませんでした</p>

          <PlanMenuFilterOpen>条件を再設定する</PlanMenuFilterOpen>
        </div>

        <Loading
          v-if="pending"
          class="absolute left-1/2 -translate-x-5 top-80"
        />
      </div>
    </div>
    <Pagination
      class="px-4 my-16"
      :current="selectedQuery.off"
      :max="plans.max"
    />
  </div>
</template>
